import { useState, FormEvent } from "react";

const tiers = [
  {
    name: "Prospect",
    price: "$50/mo",
    desc: "New riders entering the brotherhood journey.",
    features: ["Access to club rides", "Chapter meetings", "Prospect vest patch", "Mentorship program"],
  },
  {
    name: "Full Member",
    price: "$75/mo",
    desc: "Full patch holders with all rights and privileges.",
    features: ["Full patch & colors", "Voting rights", "Member discounts", "All events access", "Chapter leadership eligibility"],
    featured: true,
  },
  {
    name: "Associate",
    price: "$30/mo",
    desc: "Friends & family who ride with us but hold no patch.",
    features: ["Ride invitations", "Social events", "Associate pin", "Newsletter"],
  },
];

export default function MembershipSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7f75dnimhg6tri2v680", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="membership" className="w-full bg-[#0d0d0d] py-24 px-6 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 text-center">
          <p className="text-red-500 uppercase tracking-[0.3em] text-xs font-bold mb-3">Ride With Us</p>
          <h2 className="text-4xl md:text-5xl font-black text-white uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Membership
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto text-sm">
            Join Us
          </p>
        </div>

        {/* Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`p-8 border transition-all duration-200 ${
                tier.featured
                  ? "border-red-600 bg-[#1a0a0a]"
                  : "border-[#2a2a2a] bg-[#1a1a1a]"
              }`}
            >
              {tier.featured && (
                <span className="inline-block bg-red-700 text-white text-xs uppercase tracking-widest px-3 py-1 mb-4 font-bold">
                  Most Popular
                </span>
              )}
              <h3 className="text-2xl font-black text-white uppercase mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{tier.name}</h3>
              <p className="text-red-500 text-2xl font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{tier.price}</p>
              <p className="text-gray-500 text-sm mb-6">{tier.desc}</p>
              <ul className="space-y-3">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-gray-300 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-red-600 text-base"></i>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Application Form */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-3xl font-black text-white uppercase text-center mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Apply to Join
          </h3>
          {submitted ? (
            <div className="text-center py-12 border border-red-700 bg-[#1a0a0a]">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-red-500 text-5xl"></i>
              </div>
              <p className="text-white font-bold text-xl uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Application Received!</p>
              <p className="text-gray-400 text-sm mt-2">We&apos;ll reach out within 48 hours. Ride on.</p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">First Name</label>
                  <input name="first_name" required type="text" className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors" placeholder="John" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">Last Name</label>
                  <input name="last_name" required type="text" className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">Email</label>
                <input name="email" required type="email" className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors" placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">Phone</label>
                <input name="phone" type="tel" className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors" placeholder="+1 (555) 000-0000" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">Membership Type</label>
                <select name="membership_type" required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors cursor-pointer">
                  <option value="">Select a tier</option>
                  <option value="Prospect">Prospect - $50/mo</option>
                  <option value="Full Member">Full Member - $75/mo</option>
                  <option value="Associate">Associate - $30/mo</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">Tell Us About Yourself</label>
                <textarea
                  name="about"
                  rows={4}
                  maxLength={500}
                  className="w-full bg-[#1a1a1a] border border-[#2a2a2a] text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors resize-none"
                  placeholder="How long have you been riding? What bike do you own? Why do you want to join?"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-700 hover:bg-red-600 text-white uppercase font-bold tracking-widest py-4 text-sm transition-all duration-200 cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
